export type ObjectArraysDifferToString<T> = (value: T) => string;

export function objectArraysDiffer<T>(
    x: T[],
    y: T[],
    propOrToString: keyof T | ObjectArraysDifferToString<T>
): boolean {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let getValue: (value: T) => any;
    if (typeof propOrToString === 'string') {
        getValue = (v) => v[propOrToString];
    } else {
        getValue = propOrToString as ObjectArraysDifferToString<T>;
    }

    const xVals = x.map(getValue);
    const yVals = y.map(getValue);
    return arraysDiffer(xVals, yVals);
}

export function arraysDiffer<T>(
    x: T[],
    y: T[],
    disregardOrder = true
): boolean {
    if (x.length !== y.length) {
        return true;
    }

    const xVals = disregardOrder ? [...x].sort() : [...x];
    const yVals = disregardOrder ? [...y].sort() : [...y];
    for (let i = 0; i <= xVals.length; i++) {
        if (xVals[i] !== yVals[i]) {
            return true;
        }
    }
    return false;
}

/**
 * Function which when used as filter removes duplicate values from an array e.g.
 * [1, 3, 5, 3, 2, 1, 1, 4].filter(removeDuplicates) will return [1, 3, 5, 2, 4]
 */
export const removeDuplicates = <T>(
    value: T,
    index: number,
    array: T[]
): boolean => array.indexOf(value) === index;

/*
 * This is just a normal Javascript array, but we use this interface to emphasise its usage
 * This array is always accessed by index (i.e. items are never pushed) so there will be gaps of undefined values
 * An example is the  array of Orders in state
 * This has standard Javascipt behaviour in that the length is the last index + 1
 * But there will not be this many defined elements in the array
 */
export type IndexedArray<T> = Array<T | undefined>;

/*
 * Remove the specified index from an IndexesArray, removing trailing undefined's
 */
export function removeFromIndexedArray<T>(arr: IndexedArray<T>, index: number) {
    arr[index] = undefined;
    if (arr.length - 1 === index) {
        while (arr.length > 0 && arr[arr.length - 1] === undefined) {
            arr.pop();
        }
    }
}

/*
 * Count the defined elements in a IndexedArrap
 */
export function countIndexedArray<T>(arr: IndexedArray<T>) {
    return arr.filter((a) => !!a).length;
}

/*
 * Returns the array indexes after skipping the given skip no. of elements
 * eg. ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] with skip 2 = [0, 3, 6]
 */
export function returnIndexesAfterSkip<T>(
    arr: Array<T>,
    skip: number
): number[] {
    return arr.map((_, i) => i).filter((_, i) => i % (skip + 1) === 0);
}

/*
 * Adds an item to the head of the array while maintaining
 * a maximum size of the array. Oldest value is removed off
 * the end.
 */
export function addItemToArrayWithMaxSize<T>(
    item: T,
    array: T[],
    maxArraySize: number,
    keep?: (item: T) => boolean
): T[] {
    const itemsToKeep = keep ? array.filter(keep) : [];
    const itemsToSlice = keep ? array.filter((i) => !keep(i)) : array;
    const sliced = [item, ...itemsToSlice].slice(0, maxArraySize);
    return [...sliced, ...itemsToKeep];
}
